// src/components/landing/LandingHeader.jsx
import React, { useEffect, useRef, useState } from 'react';
import Switch from '../Switch';
import { effectiveTheme, applyTheme } from '../../utils/theme';

const LINKS = [
  ['product', 'Product'], ['how', 'How it works'], ['analyses', 'Analyses'],
  ['for-you', 'For you'],
];

/**
 * Sticky header. After ~40px of scroll it condenses and gains a shadow.
 *
 * "Condenses" without animating height: the header's box keeps its size, while a
 * decorative background layer scales down from the top and the row of content
 * rises with it. The shadow is its own layer so it can fade by opacity. All of it
 * is transform or opacity, so scrolling never triggers layout.
 */
export default function LandingHeader({ onNavigate, onSignIn, onRequestAccess }) {
  const [condensed, setCondensed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => effectiveTheme());
  const toggleRef = useRef(null);
  const firstLinkRef = useRef(null);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; setCondensed(window.scrollY > 40); });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Two switches (header and menu) and possibly another tab: follow the one event.
  useEffect(() => {
    const onTheme = (e) => setTheme(e.detail?.theme || effectiveTheme());
    window.addEventListener('decon:theme', onTheme);
    return () => window.removeEventListener('decon:theme', onTheme);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    if (firstLinkRef.current) firstLinkRef.current.focus();
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setMenuOpen(false);
      if (toggleRef.current) toggleRef.current.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // Same behaviour as the dashboard header's switch: the stored preference
  // becomes explicit the moment it is touched.
  const setMode = (wantLight) => {
    const next = wantLight ? 'light' : 'dark';
    applyTheme(next);
    setTheme(next);
  };

  const go = (id) => (e) => { e.preventDefault(); setMenuOpen(false); onNavigate(id); };

  const themeSwitch = (
    <Switch
      on={theme === 'light'}
      onChange={setMode}
      label={theme === 'light' ? 'Light' : 'Dark'}
      title={`${theme === 'light' ? 'Light' : 'Dark'} theme. Switch to ${theme === 'light' ? 'dark' : 'light'}.`}
    />
  );

  return (
    <header className={`lp-header${condensed ? ' is-condensed' : ''}`}>
      <span className="lp-header-bg" aria-hidden="true"><span className="lp-header-shadow" /></span>

      <div className="lp-wrap lp-header-row">
        <a
          className="lp-brand" href="#main" aria-label="VAUOM — back to top"
          onClick={(e) => { e.preventDefault(); window.scrollTo(0, 0); }}
        >
          <span className="logo">VAUOM</span>
        </a>

        <nav className="lp-nav" aria-label="Primary">
          <ul>
            {LINKS.map(([id, label]) => (
              <li key={id}><a href={`#${id}`} onClick={go(id)}>{label}</a></li>
            ))}
          </ul>
        </nav>

        <div className="lp-header-actions">
          <span className="lp-only-wide">{themeSwitch}</span>
          <button type="button" className="btn lp-login" onClick={onSignIn}>Login</button>
          <button type="button" className="btn primary lp-btn-primary lp-only-wide" onClick={() => onRequestAccess()}>
            Request access
          </button>
          <button
            ref={toggleRef} type="button" className="btn lp-menu-btn"
            aria-expanded={menuOpen} aria-controls="lp-drawer"
            onClick={() => setMenuOpen((v) => !v)}
          >
            Menu
          </button>
        </div>
      </div>

      <div className={`lp-scrim${menuOpen ? ' is-open' : ''}`} onClick={() => setMenuOpen(false)} aria-hidden="true" />
      <div id="lp-drawer" className={`lp-drawer${menuOpen ? ' is-open' : ''}`}>
        <nav aria-label="Menu">
          <ul>
            {LINKS.map(([id, label], i) => (
              <li key={id}>
                <a ref={i === 0 ? firstLinkRef : undefined} href={`#${id}`} onClick={go(id)}>{label}</a>
              </li>
            ))}
          </ul>
        </nav>
        <div className="lp-drawer-actions">
          {themeSwitch}
          <button
            type="button" className="btn primary lp-btn-primary"
            onClick={() => { setMenuOpen(false); onRequestAccess(); }}
          >
            Request access
          </button>
        </div>
      </div>
    </header>
  );
}
